import { type Unsubscribe } from '@kite/engine';
import { gameHash, HOME_HASH, parseRouteHash } from './route';

/** Called whenever the address names a different destination. */
export type RouteListener = (gameId: string | undefined) => void;

/**
 * The address bar as the portal's single source of truth for where it is.
 *
 * The hash rather than the History API, because the portal is a static site with no server to teach
 * about its paths, and because a hash survives being opened from a home screen shortcut. Reloading on
 * a game reopens that game, and the back gesture a tablet user already knows works without any code.
 *
 * The router only reports; deciding what a destination means belongs to the shell, and the address
 * format itself lives in `route.ts` so it can be pinned down without a page.
 */
export class Router {
  readonly #onChange: RouteListener;

  #detach: Unsubscribe | undefined;

  constructor(onChange: RouteListener) {
    this.#onChange = onChange;
  }

  /** The game named by the current address, or undefined when it names the home screen. */
  get currentGameId(): string | undefined {
    return parseRouteHash(window.location.hash);
  }

  /** Starts listening and reports the current address once, so the shell has nothing to duplicate. */
  start(): void {
    const listener = (): void => {
      this.#onChange(this.currentGameId);
    };
    window.addEventListener('hashchange', listener);
    this.#detach = () => {
      window.removeEventListener('hashchange', listener);
    };
    listener();
  }

  /** Navigates to the home screen. */
  goHome(): void {
    window.location.hash = HOME_HASH;
  }

  /** Navigates to a game by id. */
  goToGame(gameId: string): void {
    window.location.hash = gameHash(gameId);
  }

  /**
   * Replaces the current address without adding a history entry.
   *
   * Used when an address names a game that does not exist, so the back gesture does not walk straight
   * back into the same dead link.
   */
  replaceWithHome(): void {
    window.history.replaceState(null, '', HOME_HASH);
    this.#onChange(undefined);
  }

  /** Stops listening. */
  dispose(): void {
    this.#detach?.();
    this.#detach = undefined;
  }
}
